import React, { useCallback, useContext, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Select from "../../ui-components/Select";
import { TrashIcon } from "@heroicons/react/24/solid";
import Button from "../../ui-components/Button";
import Input from "../../ui-components/Input";
import { CheckIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import Placeholder from "../../ui-components/Placeholder";
import { NotificationsContext } from "../../ui-components/Notifications";
import CountryContext from "../../CountryContext";
import useCountryAxiosInstance from "../../hooks/useCountryAxiosInstance";
import PrivateImage from "../PrivateImage";
import Modal from "../../ui-components/Modal";
import TaskMapEditor from "./taskmap/TaskMapEditor";
import CloseIcon from "../CloseIcon";
import useImageUploader from "./taskmap/hooks/useImageUploader";
import TaskDetail from "../../pages/WorksitesTasks/TaskDetail";
import { MapsSectionImageUploader } from "./MapsSectionImageUploader";
import AuthenticationService from "../../services/AuthenticationService";

// Constants
const SECTIONS = ["planimetries", "photos"];

const MapsSection = ({ worksite, map_id, refreshStats }) => {
  // contexts
  const { push } = useContext(NotificationsContext);
  const { country } = useContext(CountryContext);
  const userData = AuthenticationService.getUserData();

  // state
  const [section, setSection] = useState(SECTIONS[0]);
  const [maps, setMaps] = useState(null);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [editableMapName, setEditableMapName] = useState(null);
  const [showDeleteMapDialog, setShowDeleteMapDialog] = useState(false);
  const [taskFormToShow, setTaskFormToShow] = useState(false);

  // hooks
  const intl = useIntl();
  const countryApiRequest = useCountryAxiosInstance(country.code);
  const {
    uploadingImage,
    saveCurrentMapName,
    setUploadingImage,
    onImageUploaded,
    deleteMap,
  } = useImageUploader(worksite.id, section);

  // utilities
  const sectionMaps = maps?.filter(
    (m) =>
      m.des_type === section &&
      (!search ||
        m.image?.val_description?.toLowerCase().includes(search.toLowerCase()))
  );

  const onClicktaskMapElement = useCallback(
    (type, element) => {
      if (type === "pin" && element?.id_task) {
        setTaskFormToShow(element.id_task);
      }
    },
    [setTaskFormToShow]
  );

  useEffect(() => {
    if (!selected) return;

    setEditableMapName(selected.image.val_description);
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = {
      id_worksite: worksite.id,
      join: "image",
      desc: "created_at",
    };
    countryApiRequest
      .get("/worksites_tasks_maps/all_counted", { params })
      .then((result) => {
        result = result.map(({ task_map, num_tasks }) => {
          task_map.num_tasks = num_tasks;
          return task_map;
        });

        setMaps(result);
        if (!!map_id && !isNaN(map_id)) {
          const selected = result.find(({ id }) => id === parseInt(map_id));
          if (!selected) return;
          setSection(selected.des_type);
          setSelected(selected);
        }
      })
      .catch((e) => {
        push({
          type: "error",
          title: intl.formatMessage({ id: "server_error" }),
        });
        throw e;
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Select
        label={intl.formatMessage({ id: "select_material" })}
        options={SECTIONS.map((s) => ({
          name: intl.formatMessage({ id: s }),
          value: s,
        }))}
        value={section}
        onChange={(e) => {
          setSection(e.target.value);
          setSelected(null);
        }}
      />
      <div className="grid grid-cols-12 my-3 gap-y-4">
        <div className="col-span-12 xl:col-span-3 2xl:col-span-2 flex flex-col gap-1">
          {/*---------- CONTROLS ----------*/}
          {/*
          breaking the render of whole component (with all taskmap images and links) with a subcomponent
          that handles the image choose / edit
        */}
          <MapsSectionImageUploader
            uploadingImage={uploadingImage}
            setUploadingImage={setUploadingImage}
            section={section}
            onImageChoosed={async (url) => {
              const current = await onImageUploaded(url);
              setMaps((prev) => [current, ...prev]);
              setSelected(current);
            }}
          />
          {sectionMaps?.length > 0 && (
            <div className="flex items-center gap-3 mb-1">
              <MagnifyingGlassIcon className="w-5" />
              <div className="flex-1">
                <Input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  hideBottomMargin={true}
                  placeholder={intl.formatMessage({ id: "search" }) + "..."}
                />
              </div>
            </div>
          )}
          {/*---------- MAPS LIST ----------*/}
          {!!sectionMaps
            ? sectionMaps.map((m) => {
                const classes =
                  selected?.id === m.id
                    ? `pointer-events-none bg-${userData.business}-400`
                    : "cursor-pointer hover:bg-gray-200";
                return (
                  <div
                    className={`flex bg-gray-100 items-center gap-3 rounded-lg p-3 ${classes}`}
                    onClick={() => {
                      setSelected(m);
                    }}
                    key={m.id}
                  >
                    <PrivateImage
                      image_url={m.image.val_url}
                      tag="div"
                      className="w-10 h-10 bg-cover"
                      fixedAspectRatio={false}
                      bucket="task_images"
                    />
                    <div>
                      <h3>{m.image.val_description}</h3>
                      <span className="text-xs">
                        <FormattedMessage id="associated_tasks" />:{" "}
                        {m.num_tasks || 0}
                      </span>
                    </div>
                  </div>
                );
              })
            : Array(3)
                .fill(null)
                .map((_, idx) => (
                  <Placeholder
                    key={idx}
                    classNames="rounded-lg"
                    height="h-12"
                  />
                ))}
        </div>
        <div
          className="col-span-12 xl:col-span-9 2xl:col-span-10 border-gray-200 border-t-2 mt-5 pt-5 xl:mt-0 xl:pt-0 xl:border-t-0 xl:border-l-2 xl:pl-5 xl:ml-5"
          style={{ height: "36rem" }}
        >
          {!!selected ? (
            <div className="flex flex-col gap-6 h-full">
              {/*---------- CONTROLS ----------*/}
              <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                <div className="flex-1 font-semibold">
                  <Input
                    label={
                      intl.formatMessage({ id: "name" }) +
                      (editableMapName !== selected.image.val_description
                        ? ` (${intl.formatMessage({ id: "edited" })})`
                        : "")
                    }
                    value={editableMapName}
                    onChange={(e) => {
                      setEditableMapName(e.target.value);
                    }}
                    hideBottomMargin
                  />
                </div>
                <div className="flex gap-2 mb-1.5">
                  <Button
                    full
                    styleType="default"
                    disabled={
                      !editableMapName ||
                      editableMapName === selected.image.val_description
                    }
                    onClick={async () => {
                      const updated = await saveCurrentMapName(
                        selected.image,
                        editableMapName
                      );
                      setMaps((prev) =>
                        prev.map((m) =>
                          m.id === selected.id ? { ...m, image: updated } : m
                        )
                      );
                      setSelected((prev) => ({ ...prev, image: updated }));
                    }}
                  >
                    <CheckIcon className="w-4" />
                    <FormattedMessage id="save" />
                  </Button>
                  <Button
                    full
                    styleType="danger"
                    onClick={() => {
                      setShowDeleteMapDialog(true);
                    }}
                  >
                    <TrashIcon className="w-4" />
                    <FormattedMessage id="delete" />
                  </Button>
                </div>
              </div>
              {/*---------- CANVAS ----------*/}
              <div className="flex-1">
                {taskFormToShow && (
                  <Placeholder classNames="rounded-lg" height="h-full" />
                )}
                {!taskFormToShow && !!selected?.id && (
                  <TaskMapEditor
                    url={selected.image.val_url}
                    taskMapId={selected.id}
                    onClickElement={onClicktaskMapElement}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="h-full w-full flex items-center justify-center bg-gray-100">
              <h2 className="text-lg text-gray-400">
                <FormattedMessage id="select_task_map" />
              </h2>
            </div>
          )}
        </div>
      </div>

      {showDeleteMapDialog && !!selected && (
        <Modal
          title={<FormattedMessage id="confirm_delete" />}
          description={
            <FormattedMessage
              id="confirm_delete_worksite_task_map"
              values={{
                name: (
                  <span className="font-semibold">
                    {selected.image.val_description}
                  </span>
                ),
              }}
            />
          }
          onConfirm={async () => {
            await deleteMap(selected);
            setMaps((prev) => prev.filter(({ id }) => id !== selected.id));
            setSelected(null);
            setShowDeleteMapDialog(false);
          }}
          onDismiss={() => {
            setShowDeleteMapDialog(false);
          }}
          onExit={() => {
            setShowDeleteMapDialog(false);
          }}
        />
      )}

      {taskFormToShow && (
        <>
          <div className="fixed top-0 left-0 w-full h-full z-10 bg-black opacity-50"></div>
          <div
            className={`animate-slideIn w-full sm:w-2/3 md:w-1/2 xl:w-1/3 fixed z-30 inset-y-0 right-0 bg-white overflow-y-auto shadow-2xl flex flex-col h-full`}
          >
            <div className="px-5 flex py-5">
              <h1 className="text-sm font-extrabold leading-7 text-black-500 sm:text-xl whitespace-nowrap flex gap-4 items-center">
                {intl.formatMessage({ id: "worksite_task_update_title" })}
              </h1>

              <CloseIcon
                onClick={() => {
                  setTaskFormToShow(undefined);
                }}
                text={intl.formatMessage({ id: "close" })}
              />
            </div>
            <TaskDetail
              worksite={worksite}
              changingExisting={true}
              task_id={taskFormToShow}
              onClose={() => {
                setTaskFormToShow(undefined);
              }}
              onSubmit={(close) => {
                refreshStats();
                if (close) {
                  setTaskFormToShow(undefined);
                }
              }}
            />
          </div>
        </>
      )}
    </>
  );
};

export default MapsSection;
